import api from '@/lib/api';
import type {
  PropertyImage,
  Posting,
  PostingStatus,
  ClientMedia,
  MediaType,
} from '@pms/shared';

// ─── Property images ────────────────────────────────────────────────────────

export async function fetchPropertyImages(
  propertyId: string,
): Promise<PropertyImage[]> {
  const { data } = await api.get<PropertyImage[]>(
    `/admin/properties/${propertyId}/images`,
  );
  return data;
}

export type UpsertPropertyImageBody = {
  url: string;
  caption?: string | undefined;
  sortOrder?: number | undefined;
  isCover?: boolean | undefined;
};

export async function createPropertyImage(
  propertyId: string,
  body: UpsertPropertyImageBody,
): Promise<PropertyImage> {
  const { data } = await api.post<PropertyImage>(
    `/admin/properties/${propertyId}/images`,
    body,
  );
  return data;
}

export async function updatePropertyImage(
  id: string,
  patch: Partial<UpsertPropertyImageBody>,
): Promise<PropertyImage> {
  const { data } = await api.patch<PropertyImage>(`/admin/images/${id}`, patch);
  return data;
}

export async function deletePropertyImage(id: string): Promise<void> {
  await api.delete(`/admin/images/${id}`);
}

export async function setCoverImage(id: string): Promise<PropertyImage> {
  const { data } = await api.post<PropertyImage>(`/admin/images/${id}/set-cover`);
  return data;
}

// ─── Postings / featured content ────────────────────────────────────────────

export interface CreatePostingBody {
  title: string;
  slug: string;
  body?: string | undefined;
  heroImageUrl?: string | undefined;
  featured?: boolean | undefined;
  status?: PostingStatus | undefined;
  tenantId?: string | undefined;
}

export type UpdatePostingBody = Partial<Omit<CreatePostingBody, 'tenantId'>>;

export async function fetchPostings(status?: PostingStatus): Promise<Posting[]> {
  const { data } = await api.get<Posting[]>('/admin/postings', {
    params: status ? { status } : {},
  });
  return data;
}

export async function createPosting(body: CreatePostingBody): Promise<Posting> {
  const { data } = await api.post<Posting>('/admin/postings', body);
  return data;
}

export async function updatePosting(
  id: string,
  patch: UpdatePostingBody,
): Promise<Posting> {
  const { data } = await api.patch<Posting>(`/admin/postings/${id}`, patch);
  return data;
}

export async function deletePosting(id: string): Promise<void> {
  await api.delete(`/admin/postings/${id}`);
}

export async function publishPosting(id: string): Promise<Posting> {
  const { data } = await api.post<Posting>(`/admin/postings/${id}/publish`);
  return data;
}

export async function archivePosting(id: string): Promise<Posting> {
  const { data } = await api.post<Posting>(`/admin/postings/${id}/archive`);
  return data;
}

// ─── Client media ───────────────────────────────────────────────────────────

export interface CreateClientMediaBody {
  mediaType: MediaType;
  url?: string | undefined;
  title?: string | undefined;
  body?: string | undefined;
}

export type UpdateClientMediaBody = Partial<CreateClientMediaBody>;

export async function fetchClientMedia(
  propertyId: string,
): Promise<ClientMedia[]> {
  const { data } = await api.get<ClientMedia[]>(
    `/admin/properties/${propertyId}/media`,
  );
  return data;
}

export async function createClientMedia(
  propertyId: string,
  body: CreateClientMediaBody,
): Promise<ClientMedia> {
  const { data } = await api.post<ClientMedia>(
    `/admin/properties/${propertyId}/media`,
    body,
  );
  return data;
}

export async function updateClientMedia(
  id: string,
  patch: UpdateClientMediaBody,
): Promise<ClientMedia> {
  const { data } = await api.patch<ClientMedia>(`/admin/media/${id}`, patch);
  return data;
}

export async function deleteClientMedia(id: string): Promise<void> {
  await api.delete(`/admin/media/${id}`);
}

export async function approveClientMedia(id: string): Promise<ClientMedia> {
  const { data } = await api.post<ClientMedia>(`/admin/media/${id}/approve`);
  return data;
}

export async function rejectClientMedia(id: string): Promise<ClientMedia> {
  const { data } = await api.post<ClientMedia>(`/admin/media/${id}/reject`);
  return data;
}
